import angular from 'angular';
import cloneDeep from 'lodash/cloneDeep';

import './config/errorcodes.conf';

import api from 'privilege-templates/resources/privilege-templates.api';

import { setDirtyForm } from 'app/actions/dirty-forms.actions';
import { fetchPrivilegeDisplayTypesDictionary } from 'app/actions/dictionaries/privilege-display-types-dictionary.actions';
import { fetchPrivilegeTemplates } from './actions/fetch-privilege-templates.actions';
import { deletePrivilegeTemplate } from './actions/delete-privilege-templates.actions';
import { createPrivilegeTemplate } from './actions/create-privilege-templates.actions';
import { updatePrivilegeTemplate } from './actions/update-privilege-template.actions';
import { selectRole } from './actions/privilege-templates.actions';

import helpers from './logic/helpers';

import privilegeTemplatesRoutingModule from './privilege-templates-routing-module';
import privilegeListModule from './components/privilege-list/privilege-list-module';
import privilegeItemModule from './components/privilege-item/privilege-item-module';
import PrivilegeTemplateFormModule from './components/privilege-template-form/privilege-template-form-module';
import privilegeTemplatesListModule from './components/privilege-templates-list/privilege-templates-list-module';
import privilegeTemplatesPermissionPickerModule from './components/privilege-templates-permission-picker/privilege-templates-permission-picker-module';
import PrivilegeTemplateConfirmationDialogModule from './components/privilege-template-confirmation-dialog/privilege-template-confirmation-dialog-module';

import template from './privilege-templates.html';

const DIRTY_FORM = 'privilegeTemplates';

const mapStateToThis = ({ session, privilegeTemplates, ui, dirtyForms }) => {
  const { ids, entities, selectedId } = privilegeTemplates;
  const { locale } = session;
  const { selectedLanguage } = ui;

  const preset = entities[selectedId];
  const isDirty = dirtyForms[DIRTY_FORM];

  return {
    ids,
    locale,
    preset,
    entities,
    selectedId,
    selectedLanguage,
    isDirty,
  };
};

const mapDispatchToThis = {
  selectRole,
  setDirtyForm,
  fetchPrivilegeTemplates,
  deletePrivilegeTemplate,
  createPrivilegeTemplate,
  updatePrivilegeTemplate,
  fetchPrivilegeDisplayTypesDictionary,
};

// eslint-disable-next-line no-undef
@Inject('$ngRedux', '$state', 'Notifications', 'gettextCatalog')
class PrivilegeTemplatesController {
  constructor() {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
    this.DIRTY_FORM = DIRTY_FORM;
  }

  async $onInit() {
    await this.fetchPrivilegeTemplates(this.userClass);

    this.arePrivilegesLoading = true;
    this.privileges = await api.fetchPrivileges(this.userClass);
    this.privilegesTree = helpers.createTree(this.privileges, 'displayPath');
    this.arePrivilegesLoading = false;

    this.copyPreset();
  }

  $onDestroy() {
    this.setDirtyForm(this.DIRTY_FORM, false);
    this.unsubscribe();
  }

  onLanguageSelect(locale) {
    this.fetchPrivilegeDisplayTypesDictionary(locale);
  }

  cancel() {
    this.form.$setPristine();

    this.privilegesTree = helpers.createTree(this.privileges, 'displayPath');
    this.setDirtyForm(this.DIRTY_FORM, false);
  }

  async save() {
    this.form.$setPristine();

    await this.updatePrivilegeTemplate(this.presetCopy);

    this.setDirtyForm(this.DIRTY_FORM, false);
    this.Notifications.alertSuccess(this.gettextCatalog.getString('Role saved successfully'));
  }

  async handleCreatePrivilegeTemplate() {
    const result = await this.Notifications.showComplexDialog({
      component: 'privilegeTemplateForm',
      resolve: {
        title: () => this.gettextCatalog.getString('Create Role'),
      },
    }).result;

    if (result) {
      const payload = {
        translations: result,
        userClass: this.userClass,
        privileges: [],
      };

      this.createPrivilegeTemplate(payload);
      this.Notifications.alertSuccess(this.gettextCatalog.getString('Role created successfully'));
    }
  }

  async handleDuplicatePrivilegeTemplate() {
    const result = await this.Notifications.showComplexDialog({
      component: 'privilegeTemplateForm',
      resolve: {
        title: () => this.gettextCatalog.getString('Duplicate {{ name }}', { name: this.preset.translations[this.selectedLanguage].name }),
      },
    }).result;

    if (result) {
      const payload = {
        translations: result,
        userClass: this.userClass,
        privileges: [...this.preset.privileges],
      };

      this.createPrivilegeTemplate(payload);
      this.Notifications.alertSuccess(this.gettextCatalog.getString('Role created successfully'));
    }
  }

  async handleDeletePrivilegeTemplate() {
    const result = await this.Notifications.showComplexDialog({
      component: 'privilegeTemplateConfirmationDialog',
    }).result;

    if (result) this.deletePrivilegeTemplate(this.selectedId);
  }

  onPrivilegeTemplateChange(id) {
    this.selectRole(id);
    this.cancel();
    this.copyPreset();
  }

  handleEditPrivilegeTemplate(privileges) {
    this.presetCopy = {
      ...this.presetCopy,
      privileges,
    };
  }

  copyPreset() {
    this.presetCopy = cloneDeep(this.preset);
  }
}

class PrivilegeTemplatesComponent {
  constructor() {
    this.template = template;
    this.controller = PrivilegeTemplatesController;
    this.bindings = {
      userClass: '@',
    };
  }
}

export default angular
  .module('privilege-templates', [
    'privilege-templates-errorcodes',
    privilegeListModule.name,
    privilegeItemModule.name,
    PrivilegeTemplateFormModule.name,
    privilegeTemplatesListModule.name,
    privilegeTemplatesRoutingModule.name,
    privilegeTemplatesPermissionPickerModule.name,
    PrivilegeTemplateConfirmationDialogModule.name,
  ])
  .component('privilegeTemplates', new PrivilegeTemplatesComponent());



// WEBPACK FOOTER //
// ./src/modules/privilege-templates/privilege-templates-component.js